import React, { useEffect, useState } from 'react';
import { RiDeleteBin4Fill } from 'react-icons/ri';
import { Task } from '../../../store/slices/teamFocusSessionSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

import EmptyState from '../../ui/empty-state';

interface TasksTabProps {
    tasks?: Task[];
    loading?: boolean;
    session?: any;
    teamId?: string;
    onToggleTask: (taskId: string) => void;
    onDeleteTask: (taskId: string) => void;
    onEditTask?: (task: Task) => void;
}

import teamModeApi from '../../../api/team-mode';

const TasksTab: React.FC<TasksTabProps> = ({
    tasks = [],
    loading = false,
    session,
    teamId,
    onToggleTask,
    onDeleteTask,
    onEditTask,
}) => {
    const error: string | null = null; // Error handling moved to Redux if needed, or can be passed as prop

    const user = useSelector((state: RootState) => state.user.user);

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'inprogress':
                return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'done':
                return 'bg-green-50 text-green-700 border-green-200';
            case 'todo':
            default:
                return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'inprogress': return 'In Progress';
            case 'done': return 'Done';
            case 'todo': default: return 'To Do';
        }
    };

    // Use tasks prop directly
    const displayTasks = tasks;

    return (
        <div>
            {loading && (
                <div className="flex items-center justify-center py-8 text-gray-500">Loading tasks...</div>
            )}
            {error && (
                <div className="flex items-center justify-center py-8 text-red-500">{error}</div>
            )}
            <div className="space-y-3">
                {displayTasks.map(task => {
                    const createdById = typeof task.created_by === 'object' && task.created_by !== null
                        ? task.created_by.id
                        : task.created_by;

                    const isCreator = user?.id === createdById;
                    const displayTitle = task.title || 'Untitled Task';
                    const isCompleted = task.status === 'done';

                    return (
                        <div
                            key={task.id}
                            className="flex flex-col space-y-3 p-4 border border-gray-200/60 rounded-xl hover:border-orange-200/60 hover:shadow-sm bg-white transition-all cursor-pointer"
                            onClick={() => onEditTask?.(task)}
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3 w-full">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onToggleTask(task.id); }}
                                        className={`w-6 h-6 mt-0.5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-colors ${isCompleted ? 'bg-orange-600 border-orange-600 text-white' : 'border-gray-300 hover:border-orange-400'
                                            }`}
                                    >
                                        {isCompleted && '✓'}
                                    </button>
                                    <div className="flex flex-col flex-1 min-w-0">
                                        <span className={`text-sm font-medium ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'} truncate`}>
                                            {displayTitle}
                                        </span>
                                        {task.description && (
                                            <span className="text-xs text-gray-500 mt-1 truncate block max-w-full">
                                                {task.description}
                                            </span>
                                        )}

                                        <div className="flex flex-wrap items-center mt-2.5 gap-2">
                                            <span className={`text-[10px] px-2.5 py-0.5 rounded-full border ${getStatusStyle(task.status || 'todo')} font-semibold tracking-wide uppercase`}>
                                                {getStatusLabel(task.status || 'todo')}
                                            </span>

                                            {(task.assignedTo || task.assigned_to) && (() => {
                                                const assignee = task.assignedTo ||
                                                    (typeof task.assigned_to === 'object' && task.assigned_to !== null
                                                        ? { ...task.assigned_to, avatar: '' }
                                                        : null);

                                                if (!assignee) return null;

                                                return (
                                                    <div className="flex items-center space-x-1.5 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-200">
                                                        {assignee.avatar ? (
                                                            <img src={assignee.avatar} alt={assignee.name} className="w-4 h-4 rounded-full object-cover" />
                                                        ) : (
                                                            <div className="w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center text-[8px] font-bold text-orange-600">
                                                                {assignee.name?.charAt(0).toUpperCase() || '?'}
                                                            </div>
                                                        )}
                                                        <span className="text-[10px] text-gray-600 font-medium truncate max-w-[80px]">
                                                            {assignee.name}
                                                        </span>
                                                    </div>
                                                );
                                            })()}

                                            {task.is_public && (
                                                <span className="text-[10px] text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-100 font-medium tracking-wide">
                                                    Public
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {isCreator && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onDeleteTask(task.id); }}
                                        className="text-gray-400 hover:text-red-500 flex-shrink-0 ml-3 p-1 rounded-md hover:bg-red-50 transition-colors"
                                    >
                                        <RiDeleteBin4Fill className="text-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
                {tasks.length === 0 && (
                    <EmptyState
                        icon="📝"
                        title="No tasks yet"
                        description="Create a task to keep track of what needs to be done."
                    />
                )}
            </div>
        </div>
    );
};

export default TasksTab;